import { useRef, useState } from "react";
import { Check, ArrowRight, BadgeCheck, Camera, X } from "lucide-react";
import { api, fileUrl, formatApiErrorDetail } from "../../lib/api";
import { Card, fmtDate } from "./common";

const EVENT_LABEL = { stage: "Fase", note: "Nota", restore: "Ripristino", sheet: "Scheda produzione" };

export function StageTimeline({ detail, stageLabel, onStage, onNote, canEdit, busy }) {
  const [note, setNote] = useState("");
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [upErr, setUpErr] = useState("");
  const fileRef = useRef();
  const upload = async (list) => {
    setUploading(true); setUpErr("");
    try {
      const ids = [];
      for (const f of list) {
        const fd = new FormData(); fd.append("file", f); fd.append("linked_type", "garment_fitting"); fd.append("linked_id", detail.garment.id);
        ids.push((await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } })).data.id);
      }
      setFiles(fs => [...fs, ...ids]);
    } catch (e) { setUpErr(formatApiErrorDetail(e.response?.data?.detail) || "Caricamento non riuscito"); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };
  const stages = detail.stages;
  const cur = detail.garment.sample_stage;
  const ci = stages.findIndex(s => s.key === cur);
  const next = stages[ci + 1];
  const canAdvance = canEdit && next && next.key !== "production";
  const go = async (key) => { await onStage(key, note); setNote(""); };
  return (
    <Card title="Timeline di sviluppo" testId="sampling-timeline">
      <ol className="flex flex-wrap gap-2 mb-5" data-testid="sampling-stages">
        {stages.map((s, i) => (
          <li key={s.key} className="flex items-center gap-2">
            <button type="button" disabled={!canEdit || s.key === "production" || cur === "production" || busy} onClick={() => s.key !== cur && go(s.key)}
              className={`px-3 py-2 text-xs uppercase tracking-widest border inline-flex items-center gap-2 transition-colors ${i === ci ? "bg-[color:var(--text-primary)] text-[color:var(--surface,#fff)]" : i < ci ? "opacity-80" : "opacity-50"}`}
              style={{ borderColor: "var(--border)" }} data-testid={`stage-${s.key}`} title={s.key === "production" ? "Si attiva generando la scheda produzione" : `Imposta fase ${s.label}`}>
              {i < ci && <Check size={12}/>}{s.label}
            </button>
            {i < stages.length - 1 && <ArrowRight size={12} className="opacity-30"/>}
          </li>
        ))}
      </ol>
      {canEdit && cur !== "production" && (
        <div className="flex flex-wrap gap-3 items-center mb-5">
          <input className="input-dark flex-1 min-w-[220px]" placeholder={cur === "fitting" ? "Note del fitting (correzioni da fare…)" : "Nota sulla fase (facoltativa)"} value={note} onChange={e => setNote(e.target.value)} data-testid="stage-note"/>
          {canAdvance && next.key !== "approved" && <button disabled={busy} onClick={() => go(next.key)} className="btn-gold inline-flex items-center gap-2" data-testid="stage-next">Passa a {next.label} <ArrowRight size={14}/></button>}
          {next?.key === "approved" && <button disabled={busy} onClick={() => go("approved")} className="btn-gold inline-flex items-center gap-2" data-testid="stage-approve"><BadgeCheck size={14}/> Approva campione</button>}
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={e => e.target.files?.length && upload([...e.target.files])} data-testid="stage-photo-input"/>
          <button type="button" disabled={uploading} onClick={() => fileRef.current?.click()} className="btn-ghost inline-flex items-center gap-2" data-testid="stage-add-photo"><Camera size={14}/> {uploading ? "Caricamento…" : "Foto"}</button>
          <button disabled={busy || uploading || (!note.trim() && !files.length)} onClick={async () => { await onNote(note, files); setNote(""); setFiles([]); }} className="btn-ghost" data-testid="stage-add-note">Salva nota</button>
        </div>
      )}
      {(files.length > 0 || upErr) && (
        <div className="flex flex-wrap gap-2 mb-5" data-testid="stage-pending-photos">
          {files.map(fid => (
            <div key={fid} className="relative">
              <img src={fileUrl(fid, "thumb")} alt="" className="w-16 h-16 object-cover border" style={{ borderColor: "var(--border)" }}/>
              <button onClick={() => setFiles(fs => fs.filter(x => x !== fid))} className="absolute -top-2 -right-2 bg-white border rounded-full p-0.5" data-testid={`stage-photo-remove-${fid}`}><X size={10}/></button>
            </div>
          ))}
          {upErr && <span className="text-xs text-red-600">{upErr}</span>}
        </div>
      )}
      <div className="label-caps mb-2">Storico</div>
      <ul className="text-sm space-y-2 max-h-56 overflow-auto" data-testid="sampling-events">
        {detail.events.length === 0 && <li className="opacity-60 text-xs">Nessun evento ancora.</li>}
        {detail.events.map(e => (
          <li key={e.id} className="border-b pb-2 last:border-0" style={{ borderColor: "var(--border)" }}>
            <span className="text-xs opacity-60">{fmtDate(e.at)} · {e.by_name || "—"} · {EVENT_LABEL[e.type] || e.type}</span>
            <div>{e.type === "stage" ? <>{stageLabel(e.from_stage)} → <b>{stageLabel(e.to_stage)}</b></> : null}{e.note ? <span className={e.type === "stage" ? "opacity-70" : ""}>{e.type === "stage" ? " — " : ""}{e.note}</span> : null}</div>
            {e.file_ids?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2" data-testid={`event-photos-${e.id}`}>
                {e.file_ids.map(fid => <a key={fid} href={fileUrl(fid)} target="_blank" rel="noreferrer"><img src={fileUrl(fid, "thumb")} alt="" className="w-20 h-20 object-cover border hover:opacity-80" style={{ borderColor: "var(--border)" }}/></a>)}
              </div>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}
